import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap, switchMap, catchError, from, of, debounceTime } from 'rxjs';
import { TemplateService } from '@features/catalog/services';
import { SolarTemplate } from '@features/catalog/models';
import { ToastService } from '@shared/services/toast.service';

/**
 * Template Store State
 */
interface TemplateState {
  templates: SolarTemplate[];
  selectedTemplate: SolarTemplate | null;
  loading: boolean;
  error: string | null;
  currentOrganizationId: string | null;
}

/**
 * Template Store
 * Manages state for solar analysis templates
 */
export const TemplateStore = signalStore(
  { providedIn: 'root' },
  withState<TemplateState>({
    templates: [],
    selectedTemplate: null,
    loading: false,
    error: null,
    currentOrganizationId: null,
  }),
  withComputed((state) => ({
    allTemplates: computed(() => state.templates()),
    isLoading: computed(() => state.loading()),
    hasError: computed(() => !!state.error()),
    templateCount: computed(() => state.templates().length),
    favoriteTemplates: computed(() =>
      state.templates().filter((t) => t.hubMetadata?.favorite),
    ),
  })),
  withMethods(
    (
      store,
      templateService = inject(TemplateService),
      toastService = inject(ToastService),
    ) => ({
      // RxMethod that handles organization changes automatically
      loadTemplatesForOrg: rxMethod<string>(
        pipe(
          debounceTime(0), // Debounce to prevent rapid successive calls
          tap((organizationId) => {
            // Only load if it's a different organization
            if (store.currentOrganizationId() === organizationId) {
              return;
            }
            patchState(store, { loading: true, error: null });
          }),
          switchMap((organizationId) => {
            // Skip if already loaded for this org
            if (store.currentOrganizationId() === organizationId) {
              return of(null);
            }

            return from(templateService.getAll(organizationId)).pipe(
              tap((templates) => {
                patchState(store, {
                  templates,
                  loading: false,
                  currentOrganizationId: organizationId,
                });
              }),
              catchError((error) => {
                patchState(store, {
                  loading: false,
                  error: error.message || 'Fehler beim Laden der Vorlagen',
                });
                toastService.error('Fehler beim Laden der Vorlagen');
                return of(null);
              }),
            );
          }),
        ),
      ),

      async createTemplate(organizationId: string, data: any) {
        patchState(store, { loading: true, error: null });
        try {
          const newTemplate = await templateService.create(
            organizationId,
            data,
          );
          patchState(store, {
            templates: [...store.templates(), newTemplate],
            loading: false,
          });
          toastService.success('Vorlage erfolgreich erstellt');
          return newTemplate;
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Erstellen der Vorlage',
          });
          toastService.error('Fehler beim Erstellen der Vorlage');
          throw error;
        }
      },

      async updateTemplate(
        organizationId: string,
        templateId: string,
        data: any,
      ) {
        patchState(store, { loading: true, error: null });
        try {
          const updatedTemplate = await templateService.update(
            organizationId,
            templateId,
            data,
          );
          patchState(store, {
            templates: store
              .templates()
              .map((t) => (t.id === templateId ? updatedTemplate : t)),
            loading: false,
          });
          toastService.success('Vorlage erfolgreich aktualisiert');
          return updatedTemplate;
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Aktualisieren der Vorlage',
          });
          toastService.error('Fehler beim Aktualisieren der Vorlage');
          throw error;
        }
      },

      async deleteTemplate(organizationId: string, templateId: string) {
        patchState(store, { loading: true, error: null });
        try {
          await templateService.delete(organizationId, templateId);
          patchState(store, {
            templates: store.templates().filter((t) => t.id !== templateId),
            loading: false,
          });
          toastService.success('Vorlage erfolgreich gelöscht');
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Löschen der Vorlage',
          });
          toastService.error('Fehler beim Löschen der Vorlage');
          throw error;
        }
      },

      async duplicateTemplate(
        organizationId: string,
        templateId: string,
        newName?: string,
      ) {
        patchState(store, { loading: true, error: null });
        try {
          const duplicated = await templateService.duplicate(
            organizationId,
            templateId,
            newName,
          );
          patchState(store, {
            templates: [...store.templates(), duplicated],
            loading: false,
          });
          toastService.success('Vorlage erfolgreich dupliziert');
          return duplicated;
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Duplizieren der Vorlage',
          });
          toastService.error('Fehler beim Duplizieren der Vorlage');
          throw error;
        }
      },

      async syncTemplates(organizationId: string) {
        patchState(store, { loading: true, error: null });
        try {
          const result = await templateService.syncTemplates(organizationId);
          // Reload templates after sync
          const templates = await templateService.getAll(organizationId);
          patchState(store, {
            templates,
            loading: false,
          });
          toastService.success(
            `Sync erfolgreich: ${result.templates.length} Vorlagen aktiv, ${result.modules} Module, ${result.inverters} Wechselrichter, ${result.storages} Speicher`,
          );
          return result;
        } catch (error: any) {
          patchState(store, {
            loading: false,
            error: error.message || 'Fehler beim Synchronisieren der Vorlagen',
          });
          toastService.error('Fehler beim Synchronisieren der Vorlagen');
          throw error;
        }
      },

      selectTemplate(template: SolarTemplate | null) {
        patchState(store, { selectedTemplate: template });
      },

      clearError() {
        patchState(store, { error: null });
      },
    }),
  ),
);
